/**
 * quickEmailTemplates — the default templates shipped with the Quick
 * Email flow. Each template is scoped to one or more button sites
 * (where it's offered as an option) and carries a subject + body with
 * merge tokens.
 *
 * Templates are deliberately friendly-but-professional; the copy is
 * what a solo planner would write to a warm lead. Tone dials slightly
 * by event_type (wedding = warmer, corporate = crisp) via the `tones`
 * field — consumers pick the matching tone when the context knows.
 *
 * Planners can override or add to these in Settings → Templates → Quick
 * Email (future milestone). For now these are read-only defaults.
 *
 * `site` keys:
 *   lead_reply           → first touch on a new lead
 *   lead_nudge           → following up on silent lead
 *   quote_nudge          → nudge on an un-responded quote
 *   contract_send        → "sign the contract" email
 *   contract_nudge       → nudge on an un-signed contract
 *   vendor_request       → asking a vendor for availability
 *   invoice_reminder     → reminder for overdue invoice
 *   post_event_thankyou  → thank-you + review request after wrap
 *   planning_kickoff     → T-30 planning phase kickoff
 *   event_intake_request → ask client to fill the intake form
 */

export const QE_TEMPLATES = [
  // ── 01 · First reply to a new lead ──────────────────────────────
  {
    key: 'lead_reply',
    label: 'First reply',
    site: 'lead_reply',
    subject: 'Re: Your {event_date} {event_type} — next steps',
    body: [
      'Hi {client_first_name},',
      '',
      "Thanks so much for reaching out — it means a lot that you'd consider us for your {event_type}.",
      '',
      "A {event_type} on {event_date}{venue_suffix} is absolutely something I'd love to explore with you. I have availability and could hold the date for you for the next 7 days while we chat.",
      '',
      'Would a 30-minute discovery call this week work? Here’s my calendar: {calendar_link}',
      '',
      'Looking forward to it,',
      '{planner_first_name}',
    ].join('\n'),
  },

  // ── 02 · Lead nudge (silent lead) ───────────────────────────────
  {
    key: 'lead_nudge',
    label: 'Lead nudge',
    site: 'lead_nudge',
    subject: 'Quick follow-up on your {event_type}',
    body: [
      'Hi {client_first_name},',
      '',
      "Just circling back — wanted to make sure my note didn't get buried. No rush at all; happy to chat whenever you have a moment.",
      '',
      "If it's easier, feel free to grab a time directly: {calendar_link}",
      '',
      'Best,',
      '{planner_first_name}',
    ].join('\n'),
  },

  // ── 03 · Quote nudge ────────────────────────────────────────────
  {
    key: 'quote_nudge',
    label: 'Quote nudge',
    site: 'quote_nudge',
    subject: 'Checking in on your proposal',
    body: [
      'Hi {client_first_name},',
      '',
      'Just checking in on the proposal I sent over. Happy to answer any questions or tweak anything that isn’t quite right.',
      '',
      'Quote: {quote_link}',
      '',
      "Let me know what you're thinking!",
      '',
      'Warmly,',
      '{planner_first_name}',
    ].join('\n'),
  },

  // ── 04 · Contract ready for signature ───────────────────────────
  {
    key: 'contract_send',
    label: 'Send for signature',
    site: 'contract_send',
    subject: 'Your {event_type} agreement, ready to sign',
    body: [
      'Hi {client_first_name},',
      '',
      "Here's the planning agreement for your {event_type} on {event_date}. Once you sign and the deposit lands, the date is officially held.",
      '',
      'Sign here: {contract_link}',
      '',
      'Deposit: {deposit_amount}',
      '',
      "Let me know if anything needs clarifying — I'm happy to hop on a quick call before you sign.",
      '',
      'Cheers,',
      '{planner_first_name}',
    ].join('\n'),
  },

  // ── 04b · Contract nudge (unsigned contract) ────────────────────
  // Sibling of quote_nudge. Fires from BookingWorkflowPage when the
  // contract is sent/viewed but not yet signed — the NextStepCard's
  // "Send Reminder" CTA on the `contract` stage.
  {
    key: 'contract_nudge',
    label: 'Contract nudge',
    site: 'contract_nudge',
    subject: 'Following up on your {event_type} contract',
    body: [
      'Hi {client_first_name},',
      '',
      "Just circling back on the agreement for your {event_type} on {event_date} — wanted to make sure it didn't slip through the cracks. Happy to walk through anything before you sign.",
      '',
      'Sign here: {contract_link}',
      '',
      'Let me know if any questions come up!',
      '',
      'Warmly,',
      '{planner_first_name}',
    ].join('\n'),
  },

  // ── 05 · Vendor request availability ────────────────────────────
  {
    key: 'vendor_request',
    label: 'Request availability',
    site: 'vendor_request',
    subject: 'Availability check — {event_date}',
    body: [
      'Hi there,',
      '',
      "I'm working on a {event_type} for {event_date}{venue_suffix} and would love to see if you’re available.",
      '',
      'Quick summary:',
      '  · Date: {event_date}',
      '  · Venue: {venue_name}',
      '  · Guest count: ~{guest_count}',
      '',
      "If it's a yes, I can send over more detail and we can talk scope + pricing. If it's a no, no worries — thanks for considering.",
      '',
      'Thanks,',
      '{planner_first_name}',
      '{studio_name}',
    ].join('\n'),
  },

  // ── 05b · Vendor portal invite (bulk) ───────────────────────────
  // Sent from the Vendor Hub "Email link to all" action. BCC mode so
  // vendors don't see each other's addresses. Lands in the planner's
  // Gmail drafts thanks to the mailto handoff.
  {
    key: 'vendor_portal_invite',
    label: 'Share portal link',
    site: 'vendor_request',
    subject: 'Your vendor portal · {event_type} on {event_date}',
    body: [
      'Hi team,',
      '',
      "We're officially {days_until_event} days out from the {event_type} on {event_date}{venue_suffix}. I've set up a shared portal where you can see your timeline, drop COI/COC, and stay in sync with everyone else on the event.",
      '',
      'Your portal: {portal_link}',
      '',
      "First time in? Pick your company from the roster and you'll land on your slice — the timeline filtered to your @mentions, your docs, and a thread with me. If anything's wrong or missing, reply to this email and I'll fix it on my end.",
      '',
      'Thanks,',
      '{planner_first_name}',
      '{studio_name}',
    ].join('\n'),
  },

  // ── 05c · Vendor COI request ────────────────────────────────────
  // Fired from the per-row "Request COI" action and the bulk-select
  // "Request COI" button in the Vendor Hub. Venues almost universally
  // require certificates of insurance before load-in — chasing these
  // is one of the more tedious parts of vendor wrangling, so the
  // template keeps it friendly and gives them two ways to deliver
  // (portal upload or email reply).
  {
    key: 'vendor_request_coi',
    label: 'Request COI',
    site: 'vendor_request',
    subject: 'COI needed — {event_type} on {event_date}',
    body: [
      'Hi there,',
      '',
      "Quick one: I need your current Certificate of Insurance on file for the {event_type} on {event_date}{venue_suffix}. The venue won't let anyone load in without it, so I'd love to get this locked before we're on site.",
      '',
      'Easiest path — drop the PDF in your vendor portal:',
      '{portal_link}',
      '',
      "Or just reply to this email with the file attached. Whatever's quicker on your end.",
      '',
      'Thanks so much,',
      '{planner_first_name}',
      '{studio_name}',
    ].join('\n'),
  },

  // ── 05d · Vendor timeline share ─────────────────────────────────
  // Fired from the per-row "Send Timeline Link" action and the bulk
  // "Send timeline" button. Points vendors at the shared portal where
  // the timeline is pre-filtered to just their tagged rows, so they
  // only see what's relevant to them (not the full 87-row master).
  {
    key: 'vendor_send_timeline',
    label: 'Send timeline',
    site: 'vendor_request',
    subject: 'Your timeline for {event_date}{venue_suffix}',
    body: [
      'Hi there,',
      '',
      "We're {days_until_event} days out from the {event_type} on {event_date}{venue_suffix} — here's the running timeline so you can line up load-in, coverage, and strike on your end.",
      '',
      'Your portal: {portal_link}',
      '',
      "The portal shows the timeline filtered to just your tagged rows, plus any row where you're @-mentioned. If anything's off — start time, strike, coverage window — reply here and I'll update the master.",
      '',
      'Thanks,',
      '{planner_first_name}',
      '{studio_name}',
    ].join('\n'),
  },

  // ── 06 · Invoice reminder ───────────────────────────────────────
  {
    key: 'invoice_reminder',
    label: 'Payment reminder',
    site: 'invoice_reminder',
    subject: 'Friendly reminder — invoice due',
    body: [
      'Hi {client_first_name},',
      '',
      "Just a friendly nudge about the invoice I sent over — it was due {due_date} and I wanted to make sure it didn't slip through the cracks.",
      '',
      'Amount: {amount_due}',
      'Pay here: {invoice_link}',
      '',
      'If there’s any trouble on your end, let me know and we can figure it out together.',
      '',
      'Thanks,',
      '{planner_first_name}',
    ].join('\n'),
  },

  // ── 07 · Post-event thank-you + review ──────────────────────────
  {
    key: 'post_event_thankyou',
    label: 'Thank-you + review',
    site: 'post_event_thankyou',
    subject: 'Thank you — it was a joy',
    body: [
      'Hi {client_first_name},',
      '',
      "What a weekend. It was an absolute joy to be part of your {event_type}, and I'm still floating a little.",
      '',
      "If you have a moment in the next week or two, a quick review would mean the world — it's how most new couples find us:",
      '{portal_link}',
      '',
      "I'll send along the final documents in the next few days. In the meantime, please give my best to everyone.",
      '',
      'With gratitude,',
      '{planner_first_name}',
    ].join('\n'),
  },

  // ── 07b · Client intake form request ────────────────────────────
  // Sent from Event → Clients tab when the planner hits "Send Intake
  // Form". Token-gated link hits /intake/:token; the client fills the
  // form and the response lands back in the event's client row.
  {
    key: 'event_intake_request',
    label: 'Send intake form',
    site: 'event_intake_request',
    subject: 'A few details for your {event_type} — quick intake form',
    body: [
      'Hi {client_first_name},',
      '',
      "Before we dive in, I'd love to grab a few details about your {event_type} on {event_date} so I can hit the ground running. It takes about five minutes — names, phone numbers, venue, guest count, that kind of thing.",
      '',
      'Your form: {intake_link}',
      '',
      "Everything saves as you go, so you can stop and come back whenever. Anything that's still TBD, leave blank and we'll fill it in together.",
      '',
      'Thanks,',
      '{planner_first_name}',
    ].join('\n'),
  },

  // ── 08 · Planning kickoff (T-30) ────────────────────────────────
  {
    key: 'planning_kickoff',
    label: 'Planning kickoff',
    site: 'planning_kickoff',
    subject: "We're officially in planning mode — {event_type}",
    body: [
      'Hi {client_first_name},',
      '',
      "We're officially {days_until_event} days out — time to kick things into gear. I've just unlocked the planning portal for you:",
      '',
      '{portal_link}',
      '',
      "You'll find the running timeline, your vendor list, and the handful of decisions I need from you this week. Let's grab 45 minutes this week to walk through it together — here's my calendar: {calendar_link}",
      '',
      "Can't wait.",
      '',
      '{planner_first_name}',
    ].join('\n'),
  },
]

/**
 * Return the template list offered at a given site. Sites can have
 * multiple templates (e.g. "Reply" + "Nudge") — the first entry is
 * the default selected when the modal opens.
 */
export function templatesForSite(site) {
  if (!site) return QE_TEMPLATES
  const direct = QE_TEMPLATES.filter(t => t.site === site)
  if (direct.length) return direct
  return QE_TEMPLATES
}

/**
 * Lookup by key. Returns null if unknown.
 */
export function templateByKey(key) {
  return QE_TEMPLATES.find(t => t.key === key) || null
}
